import React, { ReactNode } from 'react';
import { Button, Typography } from '@mui/material';
import { default as CloseIcon } from '@mui/icons-material/Close';
import { default as DeleteIcon } from '@mui/icons-material/DeleteOutlineOutlined';
import { default as EditIcon } from '@mui/icons-material/Edit';
import { generateDataTestId } from 'src/utils';

interface MediaEditDeleteButtonsProps {
  dataProps?: any;
  isConfirmCancel: boolean;
  setIsConfirmCancel: (newValue: boolean) => void;
  editCallback: () => void;
  deleteCallback: () => void;
  hideEdit?: boolean;
  hideDelete?: boolean;
  isEditDisabled?: boolean;
  disabledIcon?: ReactNode;
  disabledText?: string;
  isSmallDisplay?: boolean;
}

interface MediaProps {
  MODIFIED_MEDIA_CATEGORY: string;
  MODIFIED_MEDIA_DISPLAY_ORDER: number;
  MEDIA_CATEGORY: string;
  MEDIA_DISPLAY_ORDER: number;
}

const processMediaCategory = (
  dataProps?: MediaProps,
): MediaProps | undefined => {
  if (!dataProps) return undefined;

  const result = { ...dataProps }; // Create a copy to avoid mutating the original

  if (result.MEDIA_CATEGORY?.toLowerCase() === 'unbranded virtual tour') {
    result.MODIFIED_MEDIA_CATEGORY = 'virtualTour';
  } else {
    result.MODIFIED_MEDIA_CATEGORY = result.MEDIA_CATEGORY?.toLowerCase();
  }

  if (
    result.MODIFIED_MEDIA_CATEGORY === 'document' ||
    result.MODIFIED_MEDIA_CATEGORY === 'virtualTour'
  ) {
    result.MODIFIED_MEDIA_DISPLAY_ORDER = result.MEDIA_DISPLAY_ORDER ?? 0;
  } else {
    result.MODIFIED_MEDIA_DISPLAY_ORDER = result.MEDIA_DISPLAY_ORDER;
  }
  return result;
};

const MediaEditDeleteButtons: React.FC<{
  props: MediaEditDeleteButtonsProps;
}> = ({ props }) => {
  const {
    isConfirmCancel,
    setIsConfirmCancel,
    editCallback,
    deleteCallback,
    hideEdit,
    hideDelete,
    isEditDisabled,
    disabledIcon,
    disabledText,
    isSmallDisplay,
    dataProps,
  } = props;

  const processedProps = processMediaCategory(dataProps);
  return (
    <>
      {!isConfirmCancel && (
        <>
          {!hideEdit && (
            <Button
              data-testid={generateDataTestId(
                `${processedProps?.MODIFIED_MEDIA_CATEGORY}-${processedProps?.MODIFIED_MEDIA_DISPLAY_ORDER}`,
                undefined,
                'editButton',
              )}
              className="bmls_btn"
              onClick={editCallback}
              disabled={isEditDisabled}
              startIcon={
                isEditDisabled ? (
                  disabledIcon
                ) : (
                  <EditIcon sx={{ color: 'secondary.medium' }} />
                )
              }
              sx={{
                ml: 2,
                pl: 0,
                p: isSmallDisplay ? 2 : 0,
                color: 'primary.main',
              }}
            >
              <Typography
                sx={{ color: isEditDisabled ? 'grey.400' : 'secondary.medium' }}
              >
                {isEditDisabled ? disabledText : 'Edit'}
              </Typography>
            </Button>
          )}
          {!hideDelete && (
            <Button
              data-testid={generateDataTestId(
                `${processedProps?.MODIFIED_MEDIA_CATEGORY}-${processedProps?.MODIFIED_MEDIA_DISPLAY_ORDER}`,
                undefined,
                'deleteButton',
              )}
              sx={{ mr: 2, color: 'error.medium' }}
              className="bmls_btn"
              onClick={() => setIsConfirmCancel(true)}
              startIcon={<DeleteIcon sx={{ color: 'error.medium' }} />}
            >
              <Typography sx={{ color: 'error.medium' }}>Delete</Typography>
            </Button>
          )}
        </>
      )}
      {isConfirmCancel && (
        <>
          <Button
            sx={{
              ml: 2,
              pl: 0,
              p: isSmallDisplay ? 2 : 0,
              color: 'secondary.medium',
            }}
            className="bmls_btn"
            onClick={() => setIsConfirmCancel(false)}
            startIcon={<CloseIcon sx={{ color: 'secondary.medium' }} />}
          >
            <Typography sx={{ color: 'secondary.medium' }}>Cancel</Typography>
          </Button>
          <Button
            sx={{ mr: 2, color: 'error.medium' }}
            className="bmls_btn"
            onClick={deleteCallback}
            startIcon={<DeleteIcon sx={{ color: 'error.medium' }} />}
          >
            <Typography sx={{ color: 'error.medium' }}>
              Confirm Delete
            </Typography>
          </Button>
        </>
      )}
    </>
  );
};

export default React.memo(MediaEditDeleteButtons);
